import { Link } from 'react-router-dom';
import lumadriveIcon from '../assets/LumaDrive_Icon.png';
import './Terms.css';

const sections = [
  { id: 'acceptance', n: '1', title: 'Acceptance of Terms' },
  { id: 'description', n: '2', title: 'Description of Service' },
  { id: 'incident-beta', n: '3', title: 'Incident Detection — Beta' },
  { id: 'recording-consent', n: '4', title: 'Recording Consent & Legal Compliance' },
  { id: 'legal-evidence', n: '5', title: 'Use of Footage as Legal Evidence' },
  { id: 'privacy', n: '6', title: 'Privacy and Data Storage' },
  { id: 'subscription', n: '7', title: 'Subscription, Lifetime Unlock & Free Trial' },
  { id: 'warranty', n: '8', title: 'No Warranty' },
  { id: 'data-loss', n: '9', title: 'No Responsibility for Data Loss' },
  { id: 'liability', n: '10', title: 'Limitation of Liability' },
  { id: 'ip', n: '11', title: 'Intellectual Property' },
  { id: 'governing-law', n: '12', title: 'Governing Law' },
  { id: 'changes', n: '13', title: 'Changes to These Terms' },
  { id: 'contact', n: '14', title: 'Contact' },
];

export default function Legal() {
  return (
    <div className="legal-page">
      <header className="legal-nav">
        <Link to="/" className="legal-brand">
          <img src={lumadriveIcon} alt="" />
          <span>LumaDrive</span>
        </Link>
        <Link to="/" className="legal-back">
          ← Back to site
        </Link>
      </header>

      <main className="legal-wrap">
        <aside className="legal-toc" aria-label="Table of contents">
          <div className="legal-toc-inner">
            <span className="legal-toc-label">Contents</span>
            <ol>
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>
                    <span className="legal-toc-num">{s.n}</span>
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ol>
            <Link to="/support" className="legal-toc-cross">
              Support &amp; FAQ →
            </Link>
          </div>
        </aside>

        <article className="legal-doc">
          <div className="legal-hero">
            <span className="eyebrow">Legal</span>
            <h1>Terms of Use &amp; Privacy Policy</h1>
            <p className="legal-effective">
              <strong>Effective:</strong> May 12, 2026
            </p>
          </div>

          <section id="acceptance">
            <h2>
              <span className="legal-h-num">1</span>Acceptance of Terms
            </h2>
            <p>
              By downloading, installing, or using LumaDrive (the "App"), you agree to be bound by
              these Terms. If you do not agree, do not use the App. These Terms form a legally
              binding agreement between you (the "User") and the App developer (the "Developer").
            </p>
            <p>
              Apple Inc. is a third-party beneficiary of these Terms and, upon your acceptance,
              Apple will have the right to enforce these Terms against you.
            </p>
          </section>

          <section id="description">
            <h2>
              <span className="legal-h-num">2</span>Description of Service
            </h2>
            <p>
              LumaDrive is a consumer dashcam application that uses your device's camera,
              microphone, GPS, and accelerometer to record video footage while driving. It is
              designed for personal use only and is NOT a professional, certified, or guaranteed
              safety or surveillance system. LumaDrive is not a substitute for dedicated
              in-vehicle dashcam hardware.
            </p>
          </section>

          <section id="incident-beta">
            <h2>
              <span className="legal-h-num">3</span>Incident Detection — Beta
            </h2>
            <p>
              The automatic incident detection feature is provided in BETA and is EXPERIMENTAL.
              It is NOT guaranteed to detect all incidents, impacts, or collisions. It may
              trigger false positives or fail entirely.
            </p>
            <p>
              YOU MUST NOT RELY ON INCIDENT DETECTION AS YOUR SOLE OR PRIMARY MEANS OF PROTECTING
              FOOTAGE. The Developer makes no warranty that Incident Detection will function
              correctly, consistently, or at all. Any reliance on this feature is entirely at your
              own risk. The Developer is not liable for any failure to detect, record, or preserve
              footage related to any incident.
            </p>
          </section>

          <section id="recording-consent">
            <h2>
              <span className="legal-h-num">4</span>Recording Consent &amp; Legal Compliance
            </h2>
            <p>
              Laws governing audio and video recording of other individuals vary significantly by
              country, state, and municipality. Some jurisdictions require all parties to consent
              to being recorded.
            </p>
            <p>
              YOU ARE SOLELY AND EXCLUSIVELY RESPONSIBLE for understanding and complying with all
              applicable recording laws in every jurisdiction where you use the App. The Developer
              does not provide legal advice and makes no representation that using LumaDrive in
              any jurisdiction is lawful.
            </p>
            <p>
              By using the App, you represent and warrant that your use complies with all
              applicable local, state, national, and international laws. The Developer is not
              liable for any fines, civil liability, or criminal consequences arising from your
              use of the App.
            </p>
            <p>
              Do not operate the App in a way that distracts you from safe driving. You are solely
              responsible for compliance with all traffic laws.
            </p>
          </section>

          <section id="legal-evidence">
            <h2>
              <span className="legal-h-num">5</span>Use of Footage as Legal Evidence
            </h2>
            <p>
              Dashcam footage may or may not be admissible as evidence in legal proceedings
              depending on jurisdiction, chain of custody, and how footage was obtained. The
              Developer makes no representation that footage captured by LumaDrive will be
              accepted as legal evidence by any court, insurer, or authority. Consult a qualified
              legal professional before relying on any footage in a legal matter.
            </p>
          </section>

          <section id="privacy">
            <h2>
              <span className="legal-h-num">6</span>Privacy and Data Storage
            </h2>
            <p>
              LumaDrive stores all recordings, GPS data, and metadata exclusively on your local
              device. No recordings, location data, or personal information is transmitted to the
              Developer or any third party.
            </p>
            <p>
              You are responsible for the security of your device and the footage stored on it.
              The Developer has no access to, and cannot recover, any footage stored on your
              device.
            </p>
            <p>
              Location data (GPS coordinates, speed, heading) is used solely to embed telemetry
              into video clips and is never uploaded or shared.
            </p>
          </section>

          <section id="subscription">
            <h2>
              <span className="legal-h-num">7</span>Subscription, Lifetime Unlock &amp; Free Trial
            </h2>
            <p>
              LumaDrive offers 30 minutes of free recording time (the "Free Trial"). After the
              Free Trial is exhausted, continued recording requires a LumaDrive Premium purchase,
              available in two forms:
            </p>
            <p>
              <strong>Monthly Subscription — USD $3.99 per month</strong>, auto-renewing. Your
              Apple ID account will be charged $3.99/month unless cancelled at least 24 hours
              before the end of the current billing period. Subscriptions may be managed or
              cancelled in your Apple ID Account Settings. No refund is provided for any unused
              portion of a subscription period.
            </p>
            <p>
              <strong>Lifetime Unlock — USD $29.99</strong>, a one-time non-consumable purchase
              that provides unlimited recording time permanently with no recurring charges. The
              Lifetime Unlock is tied to your Apple ID and can be restored on any device signed
              into the same account via the "Restore Purchases" option.
            </p>
            <p>
              All non-recording features — viewing, sharing, organising, exporting, and deleting
              recordings — remain fully available after the Free Trial without a Premium purchase.
            </p>
            <p>
              Pricing is subject to change with notice. Existing purchases are not affected by
              price changes.
            </p>
          </section>

          <section id="warranty">
            <h2>
              <span className="legal-h-num">8</span>No Warranty
            </h2>
            <p>
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
              EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p>
              The Developer does not warrant that: (a) the App will be uninterrupted or
              error-free; (b) defects will be corrected; (c) the App will record footage under all
              circumstances including device interruptions, low battery, system constraints, or
              iOS restrictions.
            </p>
            <p>
              LOSS OF FOOTAGE FOR ANY REASON — including app crashes, device failure, storage
              exhaustion, iOS background restrictions, phone call interruptions, or auto-deletion
              — IS NOT COVERED BY ANY WARRANTY.
            </p>
          </section>

          <section id="data-loss">
            <h2>
              <span className="legal-h-num">9</span>No Responsibility for Data Loss
            </h2>
            <p>
              THE DEVELOPER IS NOT RESPONSIBLE FOR ANY LOSS OF RECORDED FOOTAGE, VIDEO DATA, OR
              ASSOCIATED METADATA FOR ANY REASON WHATSOEVER.
            </p>
            <p>
              This includes but is not limited to: accidental deletion, automatic loop deletion,
              device failure or loss, corruption of storage, iOS system termination of the App,
              insufficient device storage, software bugs or crashes, failed recordings, incomplete
              clips, background app restrictions, operating system updates, device theft, and any
              other cause whether foreseeable or not.
            </p>
            <p>
              YOU USE LUMADRIVE AS YOUR SOLE MEANS OF CAPTURING FOOTAGE ENTIRELY AT YOUR OWN RISK.
              The Developer strongly recommends exporting and backing up any footage you consider
              important as soon as possible. The Developer provides no recovery service and cannot
              retrieve lost, deleted, or corrupted recordings under any circumstances.
            </p>
          </section>

          <section id="liability">
            <h2>
              <span className="legal-h-num">10</span>Limitation of Liability
            </h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE DEVELOPER SHALL NOT BE LIABLE
              FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY
              DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF FOOTAGE, LOSS OF PROFITS, LOSS OF
              DATA, PROPERTY DAMAGE, PERSONAL INJURY, OR DEATH ARISING OUT OF OR RELATED TO YOUR
              USE OF THE APP, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p>
              IN NO EVENT SHALL THE DEVELOPER'S TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID FOR THE
              APP OR SUBSCRIPTION IN THE THREE (3) MONTHS PRECEDING THE CLAIM.
            </p>
            <p>
              Some jurisdictions do not allow the exclusion of certain damages. In such
              jurisdictions, the Developer's liability is limited to the maximum extent permitted
              by law.
            </p>
          </section>

          <section id="ip">
            <h2>
              <span className="legal-h-num">11</span>Intellectual Property
            </h2>
            <p>
              All intellectual property rights in the App, including its design, code, and
              branding, are owned by the Developer. You are granted a limited, non-exclusive,
              non-transferable licence to use the App on your personal device. You may not
              reverse-engineer, copy, modify, or distribute the App.
            </p>
          </section>

          <section id="governing-law">
            <h2>
              <span className="legal-h-num">12</span>Governing Law
            </h2>
            <p>
              These Terms are governed by the laws of the jurisdiction in which the Developer is
              established, without regard to conflict of law provisions. Any dispute arising from
              these Terms shall be resolved in the competent courts of that jurisdiction.
            </p>
          </section>

          <section id="changes">
            <h2>
              <span className="legal-h-num">13</span>Changes to These Terms
            </h2>
            <p>
              The Developer reserves the right to update these Terms at any time. You will be
              notified of material changes upon next opening the App. Continued use after
              notification constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section id="contact">
            <h2>
              <span className="legal-h-num">14</span>Contact
            </h2>
            <p>
              For questions or concerns regarding these Terms or Privacy Policy, please contact:
            </p>
            <p>
              <a className="legal-mail" href="mailto:mukuwabaffoe@gmail.com">
                mukuwabaffoe@gmail.com
              </a>
            </p>
          </section>

          <div className="legal-footer">
            <Link to="/" className="legal-back-bottom">
              ← Back to LumaDrive
            </Link>
            <Link to="/support" className="legal-back-bottom">
              Support &amp; FAQ →
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
}
